import React, { useEffect, useState } from "react";

const SIDEBAR_PARTNERS = [
  { src: "/icons/partners-logo/contredanse_vert.svg", alt: "Contredanse" },
  { src: "/icons/partners-logo/kunstenpunt_vert.svg", alt: "Kunstenpunt", height: 34 },
  { src: "/icons/partners-logo/DCJ_vert.svg", alt: "Danscentrumjette" },
  { src: "/icons/partners-logo/RAC_vert.svg", alt: "RAC" },
  { src: "/icons/partners-logo/fwb_vert.svg", alt: "FWB" },
  { src: "/icons/partners-logo/VL_vert.svg", alt: "Vlaanderen" },
];

const Sidebar = ({
  layers,
  taxonomies,
  selectedLayers,
  selectedTags,
  toggleLayerVisibility,
  toggleTag,
  selectAllForLayer,
  historyActive = false,
  onHistoryToggle,
  onAboutOpen,
  t,
  language = "en",
  showLanguageSwitch = false,
  onLanguageChange,
  styleOverrides = {},
  viewMode = "map",
  onViewModeChange,
}) => {
  const groupedTaxonomies = layers.map((layer) => {
    const sorted = taxonomies
      .filter((tax) => tax.layer_id === layer.id)
      .slice()
      .sort((a, b) => {
        const la = (pickLabel(a, language) || "").toLowerCase();
        const lb = (pickLabel(b, language) || "").toLowerCase();
        return la.localeCompare(lb);
      });
    return { layer, taxonomies: sorted };
  });
  const [expandedLayers, setExpandedLayers] = useState(
    () => new Set(layers.map((l) => l.id))
  );
  const sidebarTagline =
    (t?.("tagline") ||
      "Cartographie du secteur de la danse en Belgique") ?? "";

  useEffect(() => {
    setExpandedLayers((prev) => {
      const next = new Set(prev);
      layers.forEach((l) => next.add(l.id));
      return next;
    });
  }, [layers]);

  const toggleLayerSection = (layerId) => {
    setExpandedLayers((prev) => {
      const next = new Set(prev);
      if (next.has(layerId)) {
        next.delete(layerId);
      } else {
        next.add(layerId);
      }
      return next;
    });
  };

  const viewportHeight = typeof window !== "undefined" ? window.innerHeight : 0;
  const isMobile =
    typeof window !== "undefined" ? window.innerWidth <= 900 : false;
  const showFilters = !historyActive;
  const sidebarHeight = isMobile ? viewportHeight : "calc(100vh - 32px)";

  return (
    <aside
      style={{
        width: 320,
        background: "rgba(255,255,255,0.98)",
        boxShadow: "0 16px 48px rgba(0,0,0,0.22)",
        filter: "drop-shadow(0 12px 30px rgba(0,0,0,0.20))",
        maxHeight: sidebarHeight,
        height: sidebarHeight,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        zIndex: 5,
        borderRadius: 8,
        margin: "16px 0 16px 16px",
        boxSizing: "border-box",
        pointerEvents: "auto",
        ...styleOverrides,
      }}
    >
      {/* Fixed header */}
      <div
        style={{
          flex: "0 0 auto",
          padding: "14px 14px 0 14px",
        }}
      >
        {/* Logo + tagline + lang switch */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            marginBottom: 10,
            gap: 8,
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              alignItems: isMobile ? "flex-start" : "center",
              gap: isMobile ? 8 : 20,
              minWidth: 0,
              flex: 1,
            }}
          >
            <img
              src="/icons/logo.svg"
              alt="Dance Map"
              style={{ height: 42, flexShrink: 0 }}
            />
            <span
              style={{
                color: "#265B4F",
                fontWeight: 400,
                fontSize: 16,
                lineHeight: 1.2,
                whiteSpace: "normal",
                wordBreak: "break-word",
                textTransform: "uppercase",
                letterSpacing: 0.3,
                marginLeft: isMobile ? 0 : 50,
              }}
            >
              {sidebarTagline}
            </span>
          </div>
          {showLanguageSwitch ? (
            <div
              style={{
                marginLeft: "auto",
                display: "flex",
                gap: 6,
                marginRight: 40,
              }}
            >
              {["fr", "nl", "en"].map((lng) => (
                <button
                  key={lng}
                  onClick={() => onLanguageChange && onLanguageChange(lng)}
                  style={{
                    border: "1px solid #d1d5db",
                    background: language === lng ? "#265b4f" : "#f8f8f8",
                    color: language === lng ? "white" : "#4a5568",
                    borderRadius: 6,
                    padding: "6px 8px",
                    fontWeight: 500,
                    cursor: "pointer",
                  }}
                >
                  {lng.toUpperCase()}
                </button>
              ))}
            </div>
          ) : null}
        </div>

        {/* EN SAVOIR + and CONTRIBUER buttons */}
        <div style={{ display: "flex", gap: 8, marginBottom: 0 }}>
          <button
            onClick={() => onAboutOpen && onAboutOpen()}
            style={{
              border: "1px solid #cfd8d3",
              background: "#265B4F",
              color: "white",
              borderRadius: 7,
              padding: "5px 9px",
              fontSize: 10,
              fontWeight: 600,
              cursor: "pointer",
              textDecoration: "none",
            }}
          >
            {t?.("learnMore") || "EN SAVOIR +"}
          </button>
          <a
            href="https://dancemap.be/DMadmin/"
            target="_blank"
            rel="noreferrer"
            style={{
              border: "1px solid #cfd8d3",
              background: "#e6ece9",
              color: "#265B4F",
              borderRadius: 7,
              padding: "5px 9px",
              fontSize: 10,
              fontWeight: 600,
              cursor: "pointer",
              textDecoration: "none",
            }}
          >
            {t?.("contribute") || "CONTRIBUER A LA CARTE"}
          </a>
        </div>
        <div
          style={{
            height: 1,
            backgroundColor: "#e2e8f0",
            margin: "12px -14px 0 -14px",
          }}
        />
      </div>

      {/* Scrollable content */}
      <div
        className="sidebar-scroll"
        style={{
          flex: "1 1 auto",
          overflowY: "auto",
          padding: "0 14px 12px 14px",
          minHeight: 0,
          scrollbarWidth: "thin",
          scrollbarColor: "#cbd5e0 transparent",
        }}
      >
        <div
          style={{ display: "flex", gap: 6, marginBottom: 12, marginTop: 18 }}
        >
          <TogglePill
            label={(t?.("information") || "Information").toUpperCase()}
            active={!historyActive}
            ariaPressed={!historyActive}
            onClick={() => onHistoryToggle && onHistoryToggle(false)}
          />
          <TogglePill
            label={(t?.("history") || "History").toUpperCase()}
            active={historyActive}
            ariaPressed={historyActive}
            onClick={() => onHistoryToggle && onHistoryToggle(true)}
          />
        </div>
        {showFilters ? (
          <>
            <SectionTitle>{t?.("view") || "Vue"}</SectionTitle>
            <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
              <TogglePill
                label={t?.("map") || "Map"}
                active={viewMode === "map"}
                ariaPressed={viewMode === "map"}
                onClick={() => onViewModeChange && onViewModeChange("map")}
              />
              <TogglePill
                label={t?.("list") || "List"}
                active={viewMode === "list"}
                ariaPressed={viewMode === "list"}
                onClick={() => onViewModeChange && onViewModeChange("list")}
              />
            </div>

            <SectionTitle>{t?.("filters") || "Filtres"}</SectionTitle>
            <div
              style={{
                fontWeight: 500,
                marginBottom: 6,
                color: "#4a5568",
                fontSize: 16,
              }}
            >
              {t?.("category") || "Catégorie"}
            </div>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 6,
                marginBottom: 12,
              }}
            >
              {layers.map((layer) => (
                <button
                  key={layer.id}
                  onClick={() => toggleLayerVisibility(layer.id)}
                  style={{
                    border: "none",
                    cursor: "pointer",
                    background: selectedLayers.includes(layer.id)
                      ? layer.color
                      : "#f1f1f1",
                    color: selectedLayers.includes(layer.id)
                      ? "white"
                      : "#4a5568",
                    borderRadius: 7,
                    padding: "6px 10px",
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: "uppercase",
                  }}
                >
                  {pickLabel(layer, language)}
                </button>
              ))}
            </div>

            {groupedTaxonomies.map(({ layer, taxonomies }) => (
              <div
                key={layer.id}
                style={{
                  marginBottom: 14,
                  paddingTop: 10,
                  borderTop: "1px solid #e2e8f0",
                }}
              >
                <button
                  onClick={() => toggleLayerSection(layer.id)}
                  style={{
                    fontWeight: 600,
                    color: "#265B4F",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    marginBottom: 6,
                    fontSize: 16,
                    background: "transparent",
                    border: "none",
                    width: "100%",
                    textAlign: "left",
                    cursor: "pointer",
                    padding: 0,
                  }}
                >
                  <span style={{ color: layer.color || "#265b4f" }}>
                    {expandedLayers.has(layer.id) ? "▾" : "▸"}
                  </span>
                  {pickLabel(layer, language)}
                </button>
                {expandedLayers.has(layer.id) ? (
                  <div
                    style={{ marginLeft: 10, color: "#688C82", fontSize: 13 }}
                  >
                    {taxonomies.map((tax) => {
                      const isSelected = selectedTags.includes(tax.code);
                      const tagColor = layer.color || tax.color || "#265b4f";
                      const label = pickLabel(tax, language);
                      const indicatorId = tax.code || tax.id;

                      return (
                        <div
                          key={tax.code || tax.id}
                          style={{
                            cursor: "pointer",
                            display: "flex",
                            justifyContent: "space-between",
                            marginBottom: 4,
                            fontWeight: 400,
                            color: "#688C82",
                          }}
                          onClick={() => toggleTag(tax.code)}
                        >
                          <span
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                            }}
                          >
                            <span
                              data-testid={
                                indicatorId
                                  ? `tag-indicator-${indicatorId}`
                                  : undefined
                              }
                              aria-hidden="true"
                              style={{
                                width: 14,
                                height: 14,
                                borderRadius: 3,
                                border: `1px solid ${tagColor}`,
                                backgroundColor: isSelected
                                  ? tagColor
                                  : "transparent",
                                color: "#ffffff",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 10,
                                fontWeight: 800,
                                lineHeight: 1,
                                flexShrink: 0,
                              }}
                            >
                              {isSelected ? "✓" : ""}
                            </span>
                            <input
                              type="checkbox"
                              readOnly
                              checked={isSelected}
                              aria-label={label}
                              style={{
                                position: "absolute",
                                opacity: 0,
                                width: 1,
                                height: 1,
                                pointerEvents: "none",
                              }}
                            />
                            {label}
                          </span>
                          <span style={{ color: "#718096" }}>
                            [{tax.count ?? tax.count === 0 ? tax.count : "-"}]
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            ))}
          </>
        ) : null}
      </div>

      {/* Fixed footer */}
      <div
        style={{
          flex: "0 0 auto",
          borderTop: "1px solid #e2e8f0",
          padding: "10px 14px 12px 14px",
          color: "#265B4F",
          fontSize: 11,
          lineHeight: 1.4,
          textTransform: "uppercase",
          letterSpacing: 0.4,
        }}
      >
        {t?.("initiativePrefix") || "Une initiative de"}{" "}
        <a
          href="https://contredanse.org/"
          target="_blank"
          rel="noreferrer"
          style={{ textDecoration: "underline", color: "inherit" }}
        >
          CONTREDANSE
        </a>{" "}
        {t?.("initiativeCollab") || "en collaboration avec le"}{" "}
        <a
          href="https://danscentrumjette.be/"
          target="_blank"
          rel="noreferrer"
          style={{ textDecoration: "underline", color: "inherit" }}
        >
          DANSCENTRUMJETTE
        </a>{" "}
        {t?.("initiativeConn") || "et développée en connexion avec le"}{" "}
        <a
          href="https://larac.be/welcome-pack"
          target="_blank"
          rel="noreferrer"
          style={{ textDecoration: "underline", color: "inherit" }}
        >
          WELCOME PACK
        </a>{" "}
        {t?.("initiativeOf") || "de la"}{" "}
        <a
          href="https://larac.be/"
          target="_blank"
          rel="noreferrer"
          style={{ textDecoration: "underline", color: "inherit" }}
        >
          RAC
        </a>
        .
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 12,
            alignItems: "center",
            justifyContent: "space-between",
            marginTop: 10,
          }}
        >
          {SIDEBAR_PARTNERS.map((logo) => (
            <img
              key={logo.src}
              src={logo.src}
              alt={logo.alt}
              style={{ height: logo.height || 28, objectFit: "contain", flex: "0 0 auto" }}
            />
          ))}
        </div>
      </div>
    </aside>
  );
};

const SectionTitle = ({ children }) => (
  <div
    style={{
      fontSize: 12,
      fontWeight: 500,
      textTransform: "uppercase",
      color: "#94a3b8",
      marginBottom: 8,
      letterSpacing: 0.5,
      paddingTop: 10,
      borderTop: "1px solid #e2e8f0",
    }}
  >
    {children}
  </div>
);

const TogglePill = ({ label, active, disabled, ariaPressed, onClick }) => (
  <button
    disabled={disabled}
    aria-pressed={ariaPressed}
    style={{
      border: active ? "none" : "1px solid #d1d5db",
      cursor: disabled ? "not-allowed" : "pointer",
      background: active ? "#265b4f" : "#E0DFDE",
      color: active ? "white" : "#4a5568",
      borderRadius: 7,
      padding: "6px 10px",
      fontSize: 11,
      fontWeight: 700,
      boxShadow: active ? "inset 0 0 0 1px #1f4a3e" : "none",
    }}
    onClick={disabled ? undefined : onClick}
  >
    {label.toUpperCase()}
  </button>
);

export default Sidebar;

const pickLabel = (obj = {}, language = "en") => {
  const order = [
    `label_${language}`,
    "label_en",
    "label_fr",
    "label_nl",
    "label",
  ];
  const val = order
    .map((k) => obj[k])
    .find((v) => typeof v === "string" && v.trim().length > 0);
  return val?.trim() || obj.code || obj.id || "";
};
